import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogBody, DialogFooter, Button, Textarea,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import type { ServicePrice } from '@/api/services';
import {
  useCreateServicePrice,
  useUpdateServicePrice,
  type ServicePricePayload,
} from '@/api/services-mutations';
import { DateInput } from '@/components/date-input';
import { NumericInput } from '@/components/numeric-input';

/** Extract a human-readable message from an axios/unknown error. */
function errorMessage(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong. Please try again.';
}

type FormState = {
  price_type: 'recurring' | 'register';
  price: string;
  effective_from: string;
  effective_to: string;
  description: string;
};

const EMPTY: FormState = { price_type: 'recurring', price: '', effective_from: '', effective_to: '', description: '' };

function toForm(p: ServicePrice): FormState {
  return {
    price_type: p.price_type,
    price: String(p.price ?? ''),
    effective_from: p.effective_from ?? '',
    effective_to: p.effective_to ?? '',
    description: p.description ?? '',
  };
}

export interface ServicePriceDialogProps {
  serviceId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set the dialog edits this row; otherwise it creates a new one. */
  price?: ServicePrice | null;
}

/**
 * Add / Edit dialog for a service price row. Creates via `useCreateServicePrice`
 * or edits via `useUpdateServicePrice` depending on whether `price` is supplied.
 * `effective_to` is optional (open-ended). Errors surface in an in-dialog Alert.
 */
export function ServicePriceDialog({ serviceId, open, onOpenChange, price }: ServicePriceDialogProps) {
  const toast = useToast();
  const isEdit = !!price;
  const [form, setForm] = useState<FormState>(EMPTY);
  const create = useCreateServicePrice(serviceId);
  const update = useUpdateServicePrice(serviceId);
  const mutation = isEdit ? update : create;

  useEffect(() => {
    if (open) {
      setForm(price ? toForm(price) : EMPTY);
      create.reset();
      update.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, price]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const priceNum = Number(form.price);
  const priceInvalid = form.price.trim() === '' || Number.isNaN(priceNum) || priceNum < 0;
  const fromInvalid = form.effective_from.trim() === '';

  const close = (next: boolean) => {
    if (mutation.isPending) return;
    onOpenChange(next);
  };

  const submit = () => {
    if (priceInvalid || fromInvalid) return;
    const payload: ServicePricePayload = {
      price_type: form.price_type,
      price: priceNum,
      effective_from: form.effective_from,
      effective_to: form.effective_to.trim() ? form.effective_to : null,
      description: form.description.trim() || undefined,
    };
    const onSuccess = () => {
      toast.success(isEdit ? 'Price updated' : 'Price added');
      close(false);
    };
    if (isEdit && price) update.mutate({ priceId: price.id, data: payload }, { onSuccess });
    else create.mutate(payload, { onSuccess });
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit price' : 'Add price'}</DialogTitle>
          <DialogDescription>Register or recurring price effective from a date.</DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {mutation.isError ? <Alert tone="danger" title="Could not save price">{errorMessage(mutation.error)}</Alert> : null}
          <Field label="Type" required>
            {(p) => (
              <Select value={form.price_type} onValueChange={(v) => set('price_type', v as FormState['price_type'])}>
                <SelectTrigger id={p.id}>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recurring">Recurring</SelectItem>
                  <SelectItem value="register">Register</SelectItem>
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field label="Price" required error={priceInvalid ? 'Enter a valid amount.' : undefined}>
            {(p) => (
              <NumericInput {...p} min={0} value={form.price} invalid={priceInvalid} onChange={(e) => set('price', e.target.value)} />
            )}
          </Field>
          <Field label="Effective from" required error={fromInvalid ? 'Required' : undefined}>
            {(p) => (
              <DateInput {...p} value={form.effective_from} invalid={fromInvalid} onChange={(e) => set('effective_from', e.target.value)} />
            )}
          </Field>
          <Field label="Effective to" description="Leave blank for an open-ended price.">
            {(p) => (
              <DateInput {...p} value={form.effective_to} onChange={(e) => set('effective_to', e.target.value)} />
            )}
          </Field>
          <Field label="Description">
            {(p) => (
              <Textarea {...p} rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" disabled={mutation.isPending} onClick={() => close(false)}>Cancel</Button>
          <Button variant="primary" loading={mutation.isPending} disabled={priceInvalid || fromInvalid} onClick={submit}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ServicePriceDialog;
